/* auto-gift.js v2.6 — ENVY-safe GWP (instant UI update)
   • Auto-add at $200+ (pre-discount), auto-remove below
   • Toasts
   • One-time reload on cart page for ADD and REMOVE (robust)
   • Also calls Shopify/ENVY update hooks so drawer/cart repaint without reloads
*/
(() => {
  /* ===== PROMO GATING ===== */
  const PROMO_ACTIVE = true;        // turn promo ON/OFF here (or set to null to use theme toggle/date window)
  const PROMO_START_ISO = null;     // optional window start
  const PROMO_END_ISO = null;       // optional window end

  /* ===== GWP CONFIG ===== */
  const THRESHOLD_CENTS = 20000;            // $200.00 => 20000 cents
  const GIFT_VARIANT_ID = 47643820196010;   // current gift variant
  const GIFT_MARKER_KEY = '_auto_gwp';
  const CART_ATTR_FLAG = '_auto_gwp_active';

  // Toasts
  const TOAST_ADD_MSG = 'Free gift added: Suede Travel Jewelry Pouch 🎁';
  const TOAST_REMOVE_MSG = 'Free gift removed (order below $200)';
  const SHOW_REMOVE_TOAST = true;

  /* ===== internals ===== */
  let isUpdating = false;
  const onCartPage = /^\/cart(\/.*)?$/.test(location.pathname);

  // Clear any stale reload flags on every fresh navigation
  document.addEventListener('DOMContentLoaded', () => {
    sessionStorage.removeItem('gwp_refreshed_add');
    sessionStorage.removeItem('gwp_refreshed_remove');
  });
  // Also when returning from bfcache
  window.addEventListener('pageshow', () => {
    sessionStorage.removeItem('gwp_refreshed_add');
    sessionStorage.removeItem('gwp_refreshed_remove');
  });

  // ---- promo logic ----
  function promoActiveNow() {
    if (typeof window.__GWP_ENABLED__ === 'boolean') return window.__GWP_ENABLED__;
    if (PROMO_ACTIVE !== null) return !!PROMO_ACTIVE;
    if (PROMO_START_ISO && PROMO_END_ISO) {
      const nowMs = (typeof window.__GWP_NOW_TS__ === 'number' ? window.__GWP_NOW_TS__ * 1000 : Date.now());
      const start = Date.parse(PROMO_START_ISO);
      const end = Date.parse(PROMO_END_ISO);
      if (!isNaN(start) && !isNaN(end)) return nowMs >= start && nowMs <= end;
    }
    return true;
  }

  // ---------- toast helpers ----------
  function ensureToast() {
    let t = document.getElementById('gwp-toast');
    if (t) return t;
    if (!document.getElementById('gwp-toast-style')) {
      const style = document.createElement('style');
      style.id = 'gwp-toast-style';
      style.textContent = `
        #gwp-toast{position:fixed;left:50%;transform:translateX(-50%) translateY(20px);bottom:20px;z-index:999999;padding:12px 16px;border-radius:999px;background:rgba(0,0,0,.85);color:#fff;font-size:14px;line-height:1.2;box-shadow:0 6px 20px rgba(0,0,0,.25);opacity:0;pointer-events:none;transition:opacity .2s,transform .2s}
        #gwp-toast.show{opacity:1;transform:translateX(-50%) translateY(0)}
        @media (prefers-reduced-motion:reduce){#gwp-toast{transition:none}}
      `;
      document.head.appendChild(style);
    }
    t = document.createElement('div');
    t.id = 'gwp-toast';
    t.setAttribute('role', 'status');
    t.setAttribute('aria-live', 'polite');
    document.body.appendChild(t);
    return t;
  }
  function showToast(msg) {
    const t = ensureToast();
    t.textContent = msg;
    t.classList.add('show');
    clearTimeout(showToast._timer);
    showToast._timer = setTimeout(() => t.classList.remove('show'), 3200);
  }

  // ---------- helpers ----------
  function isAutoGiftLine(item) {
    const v = (item.properties || {})[GIFT_MARKER_KEY];
    return v === true || v === 'true' || v === 1 || v === '1';
  }

  function softReloadOnce(key) {
    if (!onCartPage) return;
    if (sessionStorage.getItem(key) === '1') return;
    sessionStorage.setItem(key, '1');
    // Use replace() for fewer cache oddities; give ENVY 120ms to settle first
    setTimeout(() => {
      location.replace(location.href);
      setTimeout(() => sessionStorage.removeItem(key), 1500);
    }, 120);
  }
  const softReloadAfterAdd = () => softReloadOnce('gwp_refreshed_add');
  const softReloadAfterRemove = () => softReloadOnce('gwp_refreshed_remove');

  async function getCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Failed to load cart');
    return res.json();
  }
  function findGift(cart) {
    return cart.items.find((i) => i.variant_id === GIFT_VARIANT_ID);
  }
  function subtotalExcludingGift(cart) {
    return cart.items.reduce((sum, i) => {
      const line = (i.variant_id === GIFT_VARIANT_ID) ? 0 : (i.price * i.quantity);
      return sum + line;
    }, 0);
  }
  async function addGift() {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: GIFT_VARIANT_ID, quantity: 1, properties: { [GIFT_MARKER_KEY]: 'true' } })
    }).then(r => r.json());
  }
  async function changeLine(idOrKey, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: idOrKey, quantity })
    }).then(r => r.json());
  }
  async function setCartAttr(active) {
    const attrs = active ? { [CART_ATTR_FLAG]: '1' } : { [CART_ATTR_FLAG]: '' };
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: attrs })
      });
    } catch (_) {}
  }

  // Fire every theme hook we can to repaint drawers/sections
  async function repaint(cartObj) {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('ajaxCart:updated'));
    // Many themes expose Shopify.onCartUpdate(cart)
    try {
      const cart = cartObj || await getCart();
      if (window.Shopify && typeof Shopify.onCartUpdate === 'function') {
        Shopify.onCartUpdate(cart);
      }
    } catch (_) {}
  }

  // ---------- sync ----------
  async function syncGift() {
    if (isUpdating) return;
    isUpdating = true;

    try {
      const cart = await getCart();
      const qualifies = subtotalExcludingGift(cart) >= THRESHOLD_CENTS;
      const giftItem = findGift(cart);
      const cartFlagActive = (cart.attributes && cart.attributes[CART_ATTR_FLAG] === '1');
      const promoOn = promoActiveNow();

      // Promo OFF: never add; remove any auto-added gift
      if (!promoOn) {
        if (giftItem && (isAutoGiftLine(giftItem) || cartFlagActive)) {
          await changeLine(giftItem.key, 0);
          await setCartAttr(false);
          await repaint();
          if (SHOW_REMOVE_TOAST) showToast(TOAST_REMOVE_MSG);
          softReloadAfterRemove();
        }
        return;
      }

      // Promo ON
      if (qualifies && !giftItem) {
        await addGift().catch(() => {});
        await setCartAttr(true);
        await repaint();
        showToast(TOAST_ADD_MSG);
        document.dispatchEvent(new CustomEvent('gwp:added', { detail: { variantId: GIFT_VARIANT_ID } }));
        softReloadAfterAdd();          // ensure cart page reflects new line instantly
      } else if (!qualifies && giftItem && (isAutoGiftLine(giftItem) || cartFlagActive)) {
        await changeLine(giftItem.key, 0);
        await setCartAttr(false);
        await repaint();
        if (SHOW_REMOVE_TOAST) showToast(TOAST_REMOVE_MSG);
        document.dispatchEvent(new CustomEvent('gwp:removed', { detail: { variantId: GIFT_VARIANT_ID } }));
        softReloadAfterRemove();       // mirror refresh on removal
      } else if (giftItem && giftItem.quantity > 1) {
        await changeLine(giftItem.key, 1);
        await repaint();
      } else if (!qualifies && !giftItem && cartFlagActive) {
        await setCartAttr(false);
      }
    } catch (e) {
      console.warn('Auto GWP error:', e);
    } finally {
      isUpdating = false;
    }
  }

  // ---------- wiring (ENVY-safe) ----------
  document.addEventListener('DOMContentLoaded', () => {
    syncGift();
    setTimeout(syncGift, 350);
    setTimeout(syncGift, 900); // extra pass for slow-loading drawers/upsells
  });
  document.addEventListener('ajaxCart:updated', () => setTimeout(syncGift, 120));
  document.addEventListener('cart:refresh', () => setTimeout(syncGift, 120));

  // Generic hooks for cart interactions
  document.addEventListener('click', (e) => {
    const el = e.target.closest('a,button'); if (!el) return;
    const href = el.getAttribute('href') || '';
    if (href.includes('/cart/change')) setTimeout(syncGift, 350);
    if (el.matches('[data-qty-plus],[data-qty-minus],[data-remove],[name="remove"],[data-cart-remove],[name="updates[]"]')) {
      setTimeout(syncGift, 350);
    }
  });
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (form && form.action && form.action.includes('/cart')) setTimeout(syncGift, 500);
  });

  // Backstop
  setInterval(syncGift, 6000);
})();
